import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  approveDeadlineRequest,
  getDeadlineRequestStatuses,
  getDeadlineRequests,
  rejectDeadlineRequest,
  type DeadlineRequestQuery,
} from '../api/deadlineRequestsApi'
import type { DeadlineRequestApproveInput, DeadlineRequestRejectInput } from '../types'

const KEY = 'deadline-update-requests'

/**
 * `queryKey` içinde sekmenin durduğu sıra — `placeholderData` önceki sorgunun anahtarından sekmeyi
 * okur. Anahtarın sırası değişirse **bu sabit de değişmelidir**; `deadlineRequestsPage.test.tsx`'teki
 * "sekme değişiminde önceki sekmenin satırı görünmez" testi bu bağı korur.
 */
const TAB_KEY_INDEX = 3

/**
 * Termin güncelleme talebi ekranının sunucu durumu (087): filtreli/sayfalı liste + onay/ret.
 *
 * <p><b>`i18n.language` queryKey'in parçasıdır:</b> satırlardaki `statusName`/`actionStatusName`
 * sunucuda `Accept-Language`'e göre çözülür; dil değişince anahtar değişmezse önbellekten eski dildeki
 * yanıt sunulur (078/059'da belgelenen tuzak).
 *
 * <p><b>Yer tutucu (önceki veri):</b> sunucu-taraflı sayfalamada sayfa değişirken `isLoading` yeniden
 * `true` olur ve paylaşılan `DataTable`'ın iskelet dalı paginator'ı render etmediği için tablo titrer.
 * Bu yüzden önceki yanıt yer tutucu olarak kullanılır — ancak **yalnız aynı sekme içinde** (bkz.
 * `placeholderData`).
 *
 * <p><b>Onay/ret sonrası `invalidateQueries` promise'i BİLİNÇLİ olarak döndürülmez</b> (gövde `{}`
 * içinde): React Query `onSuccess`'ten dönen promise'i bekler ve mutation ancak o zaman "settled" olur;
 * beklenirse yavaş bir tazeleme `onSettled`'ı geciktirir ve satırın kilidi açılmaz (078-b tuzağı).
 *
 * <p><b>Aksiyon detayı da tazelenir</b>: onay aksiyonun terminini değiştirir ve bekleyen talep rozetini
 * düşürür; o ekran ayrı bir anahtarda (`action-detail`) durduğu için ayrıca geçersizleştirilir — aksi
 * halde kullanıcı detaya döndüğünde eski termini görürdü.
 */
export function useDeadlineRequests(query: DeadlineRequestQuery) {
  const { i18n } = useTranslation()
  const qc = useQueryClient()

  // Anahtarın ikinci segmenti 'list' — böylece onay/ret sonrası yalnız LİSTE geçersizleştirilir ve
  // filtrelerden bağımsız durum kataloğu önbellekte kalır (aksi halde prefix eşleşmesi onu da
  // düşürüyor, her karardan sonra gereksiz bir /statuses isteği atılıyordu).
  const list = useQuery({
    queryKey: [
      KEY,
      'list',
      i18n.language,
      // Sekme anahtarın parçası: iki sekme AYNI uçtan farklı kapsam çeker, aynı anahtarı paylaşırlarsa
      // "Taleplerim" sekmesi onay kuyruğunun önbelleğini gösterirdi.
      query.tab,
      query.q ?? '',
      query.statusId ?? null,
      query.page,
      query.size,
    ],
    queryFn: () => getDeadlineRequests(query),
    // `keepPreviousData` YALNIZ AYNI SEKME İÇİNDE geçerlidir (bağımsız code-review bulgusu,
    // 2026-08-14): düz `keepPreviousData` anahtar NASIL değişirse değişsin önceki yanıtı yer tutucu
    // yapar ve `isLoading` false kalır → sekme değiştirildiğinde, istek dönene kadar önceki sekmenin
    // satırları YENİ sekmenin başlığı ve sütun kümesiyle (kebab kolonu açık) ekranda kalıyordu.
    // Sızıntı değil (satırlar zaten o kullanıcının), ama "sekmeler birbirine sızmaz" kuralı ekranda
    // görünür biçimde bozuluyordu. Sayfa/filtre değişiminde titremeyi önleme faydası korunur.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[TAB_KEY_INDEX] === query.tab ? previous : undefined,
  })

  // Durum kataloğu: filtrelerden bağımsız ve dile bağlı → kendi anahtarında durur; liste her filtre
  // değişiminde yeniden çekilirken bu sorgu önbellekten servis edilir (078 deseni).
  const statuses = useQuery({
    queryKey: [KEY, 'statuses', i18n.language],
    queryFn: getDeadlineRequestStatuses,
  })

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: [KEY, 'list'] })
    // Onay aksiyonun terminini yazar ve bekleyen talep rozetini düşürür → aksiyon ekranları bayatlar.
    qc.invalidateQueries({ queryKey: ['action-detail'] })
    qc.invalidateQueries({ queryKey: ['actions'] })
    // Onay ayrıca "Aksiyon termin değişikliği" (517) izi yazar → İşlem Geçmişi de tazelenmeli.
    qc.invalidateQueries({ queryKey: ['action-logs'] })
  }

  // `onSettled`: hata yolunda DA tazelenir. 409 (başkası aynı talebi işledi) sonrası liste bayat
  // kalırsa kullanıcı hâlâ eski `modDate`'i taşıyan satırı görür ve her denemede aynı hatayı alır.
  // 400 doğrulama hatasını ekran üstlenir (`approveErrorKey`) — merkez jenerik toast basmasın.
  const approve = useMutation({
    mutationFn: ({ id, body }: { id: number; body: DeadlineRequestApproveInput }) =>
      approveDeadlineRequest(id, body),
    meta: { handledCodes: ['VALIDATION_ERROR'] },
    onSettled: () => {
      invalidateAll()
    },
  })

  const reject = useMutation({
    mutationFn: ({ id, body }: { id: number; body: DeadlineRequestRejectInput }) =>
      rejectDeadlineRequest(id, body),
    onSettled: () => {
      invalidateAll()
    },
  })

  return { list, statuses, approve, reject }
}

/**
 * Talep listesinin önbelleğini tazeler — sayfa başlığındaki "Yenile" için. Liste gövdesi ayrı bir
 * panelde durduğu için (131) başlık o panelin `refetch`'ine erişemez; anahtar kökünü geçersiz kılmak
 * açık olan sekmeyi (ve Onaylarım ekranında sekme sayısını) birlikte tazeler. Söz döndürülür:
 * "Yenile" butonu dönen isteği bekleyerek döner.
 */
export function useRefreshDeadlineRequests() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: [KEY, 'list'] })
}

/** Talep listesinin önbellek anahtar kökü — aynı kümeyi okuyan sayı sorguları (131) da bu kökü kullanır. */
export const DEADLINE_REQUESTS_LIST_KEY = [KEY, 'list'] as const
